/**
 * client/src/features/income/components/IncomeOverview.jsx
 * 
 * Income overview component that displays summary information and visualizations
 * about the user's income sources.
 */
import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { formatCurrency, calculateBucketIncome } from '../utils/incomeUtils';
import { ArrowTrendingUpIcon, CalendarIcon, ChartPieIcon } from '@heroicons/react/24/outline';

// Income type colors for the pie chart
const INCOME_COLORS = {
  SALARY: '#10B981', // green
  CONTRACT: '#3B82F6', // blue
  RENTAL: '#8B5CF6', // purple
  BUSINESS: '#EC4899', // pink
  DIVIDEND: '#F59E0B', // amber
  OTHER: '#6B7280', // gray
};

// Custom tooltip for the pie chart
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded shadow-sm">
        <p className="font-medium text-gray-700">{data.name}</p>
        <p className="text-gray-600">{formatCurrency(data.value)} / month</p>
        <p className="text-gray-500 text-sm">{data.percentage}% of total</p>
      </div>
    );
  }
  return null;
};

/**
 * Income Overview Component
 * 
 * Displays a summary of the user's income, including total monthly income,
 * income distribution by type, and monthly projections.
 * 
 * @param {Object} props
 * @param {Array} props.incomesArray - Array of income objects
 * @param {Array} props.buckets - Array of bucket objects
 */
const IncomeOverview = ({ incomesArray, buckets = [] }) => {
  const now = new Date();
  const isActiveNow = (income) => {
    if (!income) return false;
    if (income.isActive === false) return false;
    const start = income.startDate ? new Date(income.startDate) : null;
    const end = income.endDate ? new Date(income.endDate) : null;
    const started = !start || start <= now;
    const notEnded = !end || end >= now;
    return started && notEnded;
  };
  // Helper function to calculate monthly amount - MOVED UP HERE BEFORE USAGE
  const calculateMonthlyAmount = (amount, frequency) => {
    switch (frequency?.toLowerCase()) {
      case 'weekly': return amount * 4.333; // Average weeks per month
      case 'biweekly': return amount * 2.167; // Average bi-weeks per month
      case 'semimonthly': return amount * 2; // Twice per month
      case 'monthly': return amount;
      case 'quarterly': return amount / 3; // One third per month
      case 'annually':
      case 'yearly': return amount / 12; // One twelfth per month
      default: return amount;
    }
  };

  const incomeByType = useMemo(() => {
    if (!incomesArray || !Array.isArray(incomesArray) || incomesArray.length === 0) {
      return [];
    }

    // Group incomes by type
    const groupedIncomes = {};
    let totalMonthlyAmount = 0;

    incomesArray.filter(isActiveNow).forEach(income => {
      const category = income.category || 'OTHER';
      const amount = income.amount || 0;
      
      if (!groupedIncomes[category]) {
        groupedIncomes[category] = {
          name: category,
          value: 0,
          color: INCOME_COLORS[category] || INCOME_COLORS.OTHER
        };
      }
      
      // Convert to monthly
      let monthlyAmount = calculateMonthlyAmount(amount, income.frequency);
      groupedIncomes[category].value += monthlyAmount;
      totalMonthlyAmount += monthlyAmount;
    });

    // Calculate percentages
    return Object.values(groupedIncomes).map(type => ({
      ...type,
      percentage: totalMonthlyAmount > 0 
        ? Math.round((type.value / totalMonthlyAmount) * 100) 
        : 0
    }));
  }, [incomesArray]);

  // Calculate monthly income (use nested payDetails when available)
  const totalMonthlyIncome = useMemo(() => {
    if (!incomesArray || !Array.isArray(incomesArray)) {
      return 0;
    }
    const actives = incomesArray.filter(isActiveNow);
    return actives.reduce((sum, inc) => {
      const gross = inc?.typeSpecificDetails?.salary?.payDetails?.grossAmount ??
                    inc?.typeSpecificDetails?.contractor?.payDetails?.grossAmount ??
                    inc?.grossAmount ??
                    inc?.amount ?? 0;
      const freq = inc?.typeSpecificDetails?.salary?.payDetails?.frequency ??
                   inc?.typeSpecificDetails?.contractor?.payDetails?.frequency ??
                   inc?.frequency;
      return sum + calculateMonthlyAmount(gross || 0, (freq || '').toString());
    }, 0);
  }, [incomesArray]);

  // Calculate totals with gross/net separation
  const { 
    monthlyGross, 
    monthlyNet, 
    monthlyTax, 
    yearlyGross, 
    yearlyNet, 
    yearlyTax 
  } = useMemo(() => {
    if (!incomesArray || !Array.isArray(incomesArray)) {
      return { monthlyGross: 0, monthlyNet: 0, monthlyTax: 0, yearlyGross: 0, yearlyNet: 0, yearlyTax: 0 };
    }

    const actives = incomesArray.filter(isActiveNow);
    let monthlyGross = 0;
    let monthlyNet = 0;

    actives.forEach(inc => {
      const gross = inc?.typeSpecificDetails?.salary?.payDetails?.grossAmount ??
                    inc?.typeSpecificDetails?.contractor?.payDetails?.grossAmount ??
                    inc?.grossAmount ??
                    inc?.amount ?? 0;
      const net = inc?.typeSpecificDetails?.salary?.payDetails?.netAmount ??
                  inc?.typeSpecificDetails?.contractor?.payDetails?.netAmount ??
                  inc?.netAmount ?? 0;
      const freq = inc?.typeSpecificDetails?.salary?.payDetails?.frequency ??
                   inc?.typeSpecificDetails?.contractor?.payDetails?.frequency ??
                   inc?.frequency;
      monthlyGross += calculateMonthlyAmount(gross || 0, (freq || '').toString());
      if (net) {
        monthlyNet += calculateMonthlyAmount(net || 0, (freq || '').toString());
      } else {
        // fallback estimate net ≈ 77% of gross
        monthlyNet += calculateMonthlyAmount((gross || 0) * 0.77, (freq || '').toString());
      }
    });

    const monthlyTax = Math.max(monthlyGross - monthlyNet, 0);
    return {
      monthlyGross,
      monthlyNet,
      monthlyTax,
      yearlyGross: monthlyGross * 12,
      yearlyNet: monthlyNet * 12,
      yearlyTax: monthlyTax * 12,
    };
  }, [incomesArray]);

  // Annual projection
  const annualProjection = totalMonthlyIncome * 12;
  
  return (
    <div className="bg-gradient-to-br from-gray-50/95 to-white/95 backdrop-blur-md rounded-xl p-1">
      <div className="p-3 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {/* Card 1: Monthly Summary */}
        <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_15px_45px_-12px_rgba(0,0,0,0.5)] hover:shadow-[0_20px_55px_-12px_rgba(0,0,0,0.6)] rounded-xl p-3 sm:p-4 md:p-6 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-3px] hover:scale-[1.01] after:absolute after:inset-0 after:bg-gradient-to-br after:from-white/5 after:to-black/5 after:rounded-xl after:opacity-0 hover:after:opacity-100 after:transition-opacity">
          <div className="flex items-center mb-3 sm:mb-4">
            <div className="bg-gradient-to-br from-green-50 to-green-100/90 p-2 md:p-3 rounded-xl mr-2 sm:mr-3 shadow-md">
              <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-green-600" />
            </div>
            <h3 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent">Monthly Summary</h3>
          </div>
          
          <div className="space-y-2 sm:space-y-3 md:space-y-4 relative z-10">
            <div>
              <p className="text-xs md:text-sm font-semibold text-gray-500">Gross Income</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-indigo-600 drop-shadow-sm">{formatCurrency(monthlyGross)}</p>
            </div>
            
            <div>
              <p className="text-xs md:text-sm font-semibold text-gray-500">Net Income</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-600 drop-shadow-sm">{formatCurrency(monthlyNet)}</p>
            </div>
            
            <div>
              <p className="text-xs md:text-sm font-semibold text-gray-500">Estimated Tax</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-red-500 drop-shadow-sm">{formatCurrency(monthlyTax)}</p>
            </div>

            <div className="pt-2 md:pt-3 border-t border-gray-200/50">
              <div className="relative pt-1">
                <div className="flex mb-1 md:mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-bold inline-block text-gray-600">
                      Tax Rate: {monthlyGross > 0 ? Math.round((monthlyTax / monthlyGross) * 100) : 0}%
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 md:h-2.5 text-xs flex rounded-full bg-gradient-to-r from-gray-200/50 to-gray-100/50 shadow-inner">
                  <div 
                    style={{ width: `${monthlyGross > 0 ? (monthlyNet / monthlyGross) * 100 : 0}%` }} 
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-green-500 to-green-400"
                  ></div>
                  <div 
                    style={{ width: `${monthlyGross > 0 ? (monthlyTax / monthlyGross) * 100 : 0}%` }} 
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-red-500 to-red-400"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Card 2: Annual Projection */}
        <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_15px_45px_-12px_rgba(0,0,0,0.5)] hover:shadow-[0_20px_55px_-12px_rgba(0,0,0,0.6)] rounded-xl p-3 sm:p-4 md:p-6 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-3px] hover:scale-[1.01] after:absolute after:inset-0 after:bg-gradient-to-br after:from-white/5 after:to-black/5 after:rounded-xl after:opacity-0 hover:after:opacity-100 after:transition-opacity">
          <div className="flex items-center mb-3 sm:mb-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/90 p-2 md:p-3 rounded-xl mr-2 sm:mr-3 shadow-md">
              <ArrowTrendingUpIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-blue-600" />
            </div>
            <h3 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent">Annual Projection</h3>
          </div>
          
          <div className="space-y-2 sm:space-y-3 md:space-y-4 relative z-10">
            <div>
              <p className="text-xs md:text-sm font-semibold text-gray-500">Annual Gross</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-indigo-600 drop-shadow-sm">{formatCurrency(yearlyGross)}</p>
            </div>
            
            <div>
              <p className="text-xs md:text-sm font-semibold text-gray-500">Annual Net</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-600 drop-shadow-sm">{formatCurrency(yearlyNet)}</p>
            </div>
            
            <div>
              <p className="text-xs md:text-sm font-semibold text-gray-500">Annual Tax</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-red-500 drop-shadow-sm">{formatCurrency(yearlyTax)}</p>
            </div>

            <div className="pt-2 md:pt-3 border-t border-gray-200/50">
              <div className="flex justify-between mb-1 md:mb-2">
                <span className="text-xs font-bold text-gray-600">Year progress</span>
                <span className="text-xs font-bold text-gray-600">{Math.round((new Date().getMonth() + 1) / 12 * 100)}%</span>
              </div>
              <div className="w-full bg-gradient-to-r from-gray-200/50 to-gray-100/50 rounded-full h-2 md:h-2.5 shadow-inner">
                <div 
                  className="bg-gradient-to-r from-blue-600 to-blue-500 h-2 md:h-2.5 rounded-full shadow-md transition-all duration-500" 
                  style={{ width: `${Math.round((new Date().getMonth() + 1) / 12 * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Card 3: Income Distribution - Mobile Optimized */}
        <div className="relative col-span-1 sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_15px_45px_-12px_rgba(0,0,0,0.5)] hover:shadow-[0_20px_55px_-12px_rgba(0,0,0,0.6)] rounded-xl p-3 sm:p-4 md:p-6 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-3px] hover:scale-[1.01] after:absolute after:inset-0 after:bg-gradient-to-br after:from-white/5 after:to-black/5 after:rounded-xl after:opacity-0 hover:after:opacity-100 after:transition-opacity">
          <div className="flex items-center mb-3 sm:mb-4">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/90 p-2 md:p-3 rounded-xl mr-2 sm:mr-3 shadow-md">
              <ChartPieIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-purple-600" />
            </div>
            <h3 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent">Income Distribution</h3>
          </div>
          
          {incomeByType.length > 0 ? (
            <div className="h-32 sm:h-48 md:h-60">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={incomeByType}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={window.innerWidth < 640 ? 40 : 60}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percentage }) => `${percentage}%`}
                  >
                    {incomeByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 sm:h-48 md:h-60 text-gray-400">
              <p className="text-sm sm:text-base">No income data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncomeOverview; 